import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Check, MapPin, Minus, Scissors, Sparkles, Users } from 'lucide-react';
import { plansAPI } from '../../lib/api';

/**
 * Preis-Sektion der Landingpage.
 * Anzeigen beziehen ihre Preise über die öffentliche Plan-API. Die Werte hier
 * sind nur der ausfallsichere Startwert bis die API geladen ist.
 */

function buildRegisterHref(plan, billingPeriod) {
  const params = new URLSearchParams({
    plan: String(plan || 'starter').toLowerCase(),
    billing_period: billingPeriod === 'yearly' ? 'yearly' : 'monthly',
  });
  return `/register?${params.toString()}`;
}

const PLANS = [
  {
    key: 'starter',
    name: 'Starter',
    tagline: 'Für den Einstieg mit Online-Buchung',
    monthly: 49,
    yearly: 39,
    limits: [
      { icon: MapPin, label: '1 Standort' },
      { icon: Users, label: '1 Mitarbeiter' },
      { icon: Scissors, label: '10 Leistungen' },
    ],
    features: [
      'Online-Buchung rund um die Uhr',
      'Kalender mit Tag-, Wochen- und Monatsansicht',
      'Kundenverwaltung mit Historie',
      'E-Mail-Bestätigungen und Erinnerungen',
      'Online-Bezahlung über Stripe',
      'Google-Kalender-Sync',
    ],
    missing: ['Anamnese und Patientenakte', 'SMS-Erinnerungen', 'Marketing-Funktionen'],
    highlight: false,
  },
  {
    key: 'growth',
    name: 'Business',
    tagline: 'Die Praxissoftware für medizinische Ästhetik',
    monthly: 99,
    yearly: 75,
    limits: [
      { icon: MapPin, label: '2 Standorte' },
      { icon: Users, label: '5 Mitarbeiter' },
      { icon: Scissors, label: 'Unbegrenzte Leistungen' },
    ],
    features: [
      'Alles aus Starter',
      'Anamnese und Patientenakte',
      'Digitale Formulare mit Unterschrift',
      'SMS-Erinnerungen und Storno-SMS',
      'Anzahlungen und Wartelisten',
      'Instagram-Automatisierung',
      'Messenger für SMS, Chat und E-Mail',
      'E-Mail-Marketing und Gutscheine',
      'Google-Bewertungen',
      'Berichte und Auswertungen',
      'GOÄ-Rechnungen',
      'Mobile App für iOS und Android',
    ],
    missing: [],
    highlight: true,
    badge: 'Für Praxen empfohlen',
  },
  {
    key: 'pro',
    name: 'Pro',
    tagline: 'Für mehrere Standorte und Teams',
    monthly: 169,
    yearly: 129,
    limits: [
      { icon: MapPin, label: '10 Standorte, weitere zubuchbar' },
      { icon: Users, label: 'Unbegrenzte Mitarbeiter' },
      { icon: Scissors, label: 'Unbegrenzte Leistungen' },
    ],
    features: [
      'Alles aus Business',
      'Mobile App für iOS und Android',
      'Shop und Produkte',
      'Content-Bibliothek und Werbemanager',
      'Eigene Domain für die Buchungsseite',
      'API-Zugang',
    ],
    missing: [],
    highlight: false,
  },
];

const CAL_PROPS = {
  'data-cal-link': 'makeit-media/mib-erstgesprach',
  'data-cal-namespace': 'mib-erstgesprach',
  'data-cal-config': '{"layout":"month_view","useSlotsViewOnSmallScreen":"true","theme":"light"}',
};

export default function PricingSection() {
  const [billing, setBilling] = useState('yearly');
  const [planPrices, setPlanPrices] = useState({});
  const isYearly = billing === 'yearly';

  useEffect(() => {
    let active = true;
    plansAPI.listPublic()
      .then((response) => {
        if (!active) return;
        const next = {};
        (response.data?.plans || []).forEach((plan) => {
          const key = String(plan.plan || '').toLowerCase();
          next[key] = plan.display_price_amounts || {};
        });
        setPlanPrices(next);
      })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  return (
    <section id="preise" className="px-6 py-20 md:px-10 md:py-28 lg:px-14" data-testid="pricing-section">
      <div className="mx-auto max-w-[1280px]" data-reveal>
        {/* Kopf */}
        <div className="mx-auto max-w-3xl text-center">
          <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.32em] text-[#F02672]">Preise</p>
          <h2 className="overflow-visible pb-1 text-3xl font-bold leading-[1.15] tracking-tight sm:text-4xl lg:text-5xl">
            Richtige Praxissoftware kostet{' '}
            <span className="landing-gradient-text inline-block bg-gradient-to-r from-[#F05A8A] via-[#EC2B72] to-[#C83CE0] bg-clip-text pb-[0.22em] pl-[0.04em] pr-[0.16em] pt-[0.08em] italic font-light leading-[1.2] text-transparent">
              nicht mehr hunderte Euro im Monat.
            </span>
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-[#6F6067]">
            Keine Einrichtungsgebühr. Monatlich kündbar. Wir richten deine Praxis gemeinsam mit dir ein.
          </p>
        </div>

        {/* Umschalter */}
        <div className="mt-10 flex justify-center">
          <div className="inline-flex items-center gap-1 rounded-full border border-[#2A1720]/10 bg-white p-1 shadow-sm">
            <button
              type="button"
              onClick={() => setBilling('monthly')}
              className={`h-10 rounded-full px-5 text-sm font-medium transition-all ${
                !isYearly ? 'bg-[#171014] text-white shadow' : 'text-[#6F6067] hover:text-[#171014]'
              }`}
            >
              Monatlich
            </button>
            <button
              type="button"
              onClick={() => setBilling('yearly')}
              className={`h-10 rounded-full px-5 text-sm font-medium transition-all ${
                isYearly ? 'bg-[#171014] text-white shadow' : 'text-[#6F6067] hover:text-[#171014]'
              }`}
            >
              Jährlich
              <span className={`ml-2 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                isYearly ? 'bg-[#EC2B72] text-white' : 'bg-[#FADCE8] text-[#D72D6B]'
              }`}>
                bis zu 24% sparen
              </span>
            </button>
          </div>
        </div>

        {/* Karten */}
        <div className="mt-12 grid gap-6 lg:grid-cols-3 lg:items-start">
          {PLANS.map((plan) => {
            const price = Number(planPrices[plan.key]?.[isYearly ? 'yearly' : 'monthly'] ?? (isYearly ? plan.yearly : plan.monthly));
            const dark = plan.highlight;

            return (
              <div
                key={plan.key}
                className={`relative flex h-full flex-col rounded-[2rem] border p-7 md:p-8 ${
                  dark
                    ? 'border-transparent bg-[#171014] text-white shadow-[0_30px_80px_-40px_rgba(42,23,32,0.75)] lg:-mt-4 lg:pb-10'
                    : 'border-[#2A1720]/10 bg-white text-[#171014] shadow-[0_18px_60px_-40px_rgba(42,23,32,0.35)]'
                }`}
              >
                {dark && (
                  <>
                    <div className="pulse-glow pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#D72D6B]/35 blur-[90px]" />
                    <span className="absolute -top-3 left-7 inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-[#EC2B72] to-[#C83CE0] px-3.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-white shadow-lg">
                      <Sparkles className="h-3 w-3" />
                      {plan.badge}
                    </span>
                  </>
                )}

                <div className="relative">
                  <h3 className="text-2xl font-bold tracking-tight">{plan.name}</h3>
                  <p className={`mt-2 text-sm ${dark ? 'text-white/70' : 'text-[#7B6A72]'}`}>{plan.tagline}</p>

                  <div className="mt-7 flex items-end gap-1.5">
                    <span className="text-5xl font-semibold tracking-tight">{price}</span>
                    <span className={`mb-1.5 text-lg ${dark ? 'text-white/70' : 'text-[#7B6A72]'}`}>€</span>
                    <span className={`mb-2 text-sm ${dark ? 'text-white/60' : 'text-[#7B6A72]'}`}>/ Monat*</span>
                  </div>
                  <p className={`mt-1.5 text-xs ${dark ? 'text-white/55' : 'text-[#7B6A72]'}`}>
                    {isYearly ? 'im Jahresplan, jährlich abgerechnet' : 'monatlich kündbar'}
                  </p>

                  <div className={`mt-5 space-y-2 rounded-2xl px-4 py-3.5 ${
                    dark ? 'bg-white/[0.07]' : 'bg-[#F8EEF2]'
                  }`}>
                    {plan.limits.map(({ icon: Icon, label }) => (
                      <div key={label} className="flex items-center gap-2.5">
                        <Icon className={`h-3.5 w-3.5 shrink-0 ${dark ? 'text-[#F05A8A]' : 'text-[#D72D6B]'}`} strokeWidth={2.2} />
                        <span className={`text-[13px] leading-snug ${dark ? 'text-white/80' : 'text-[#5D4E55]'}`}>{label}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    {...CAL_PROPS}
                    className={`btn-shine mt-7 inline-flex h-13 w-full items-center justify-center rounded-full px-6 py-3.5 text-[15px] font-medium transition-all ${
                      dark
                        ? 'bg-[#EC2B72] text-white shadow-[0_14px_40px_-16px_rgba(236,43,114,0.85)] hover:bg-[#D72D6B]'
                        : 'border border-[#2A1720]/15 bg-white text-[#171014] shadow-sm hover:bg-[#F8EEF2]'
                    }`}
                  >
                    Demo buchen
                    <ArrowUpRight className="ml-2 h-4 w-4" />
                  </button>

                  <ul className="mt-7 space-y-3">
                    {plan.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm">
                        <span className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${
                          dark ? 'bg-[#EC2B72]' : 'bg-[#FADCE8]'
                        }`}>
                          <Check className={`h-3 w-3 ${dark ? 'text-white' : 'text-[#D72D6B]'}`} strokeWidth={3} />
                        </span>
                        <span className={dark ? 'text-white/85' : 'text-[#43353B]'}>{f}</span>
                      </li>
                    ))}
                    {plan.missing.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm">
                        <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#F1EDEE]">
                          <Minus className="h-3 w-3 text-[#A79BA1]" strokeWidth={3} />
                        </span>
                        <span className="text-[#A79BA1]">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* Netto-Hinweis */}
        <p className="mt-6 text-center text-xs text-[#7B6A72]">
          * Alle Preise sind Nettopreise in Euro und verstehen sich zzgl. der gesetzlichen Umsatzsteuer.
        </p>

        {/* Einstiegsangebot als Risiko-Abbau */}
        <div className="mt-10 rounded-[2rem] border border-[#2A1720]/10 bg-[#F8EEF2] p-7 md:p-9" data-reveal>
          <div className="grid items-center gap-7 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.32em] text-[#F02672]">Im Preis enthalten</p>
              <h3 className="text-2xl font-bold tracking-tight sm:text-[28px]">
                Wir richten deine Praxis gemeinsam mit dir ein.
              </h3>
              <p className="mt-4 max-w-2xl text-base leading-relaxed text-[#5D4E55]">
                Leistungen, Formulare, Mitarbeiter, Buchungsseite. Deine Daten aus Google Kalender oder deinem
                bisherigen Tool übernehmen wir kostenlos. Du musst nichts allein einrichten und nichts abtippen.
              </p>
              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2.5 text-sm text-[#43353B]">
                {['Persönliche Einrichtung', 'Datenübernahme kostenlos', 'Keine Einrichtungsgebühr', 'Monatlich kündbar'].map((item) => (
                  <span key={item} className="inline-flex items-center gap-2">
                    <Check className="h-4 w-4 text-[#D72D6B]" strokeWidth={3} />
                    {item}
                  </span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-4 lg:justify-self-end">
              <button
                {...CAL_PROPS}
                className="btn-shine inline-flex h-14 w-full items-center justify-center rounded-full bg-[#171014] px-8 text-[15px] font-medium text-white shadow hover:bg-[#2A1720] lg:w-auto"
              >
                Demo buchen, 30 Minuten
                <ArrowUpRight className="ml-2 h-4 w-4" />
              </button>
              <p className="mt-3 text-center text-xs text-[#7B6A72] lg:text-right">
                Direkt starten?{' '}
                <Link to={buildRegisterHref('growth', billing)} className="underline underline-offset-2 hover:text-[#171014]">
                  Jetzt registrieren
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
